/**
 * API Service Module
 * Centralized API client for backend communication
 */
import axios from 'axios';

// API base URL from environment
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Token storage keys
const TOKEN_KEY = 'isf_access_token';
const REFRESH_TOKEN_KEY = 'isf_refresh_token';
const USER_KEY = 'isf_current_user';

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Auth Service - handles authentication
 */
export const authService = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  
  setToken: (token) => localStorage.setItem(TOKEN_KEY, token),
  
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  
  setRefreshToken: (token) => localStorage.setItem(REFRESH_TOKEN_KEY, token),
  
  // Set both tokens at once (for OAuth callbacks)
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  
  getCurrentUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },
  
  setCurrentUser: (user) => localStorage.setItem(USER_KEY, JSON.stringify(user)),
  
  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),
  
  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, refresh_token } = response.data;
    
    authService.setToken(access_token);
    if (refresh_token) authService.setRefreshToken(refresh_token);
    
    // Parse user info from token for immediate login
    const tokenPayload = JSON.parse(atob(access_token.split('.')[1]));
    const userData = {
      id: tokenPayload.sub,
      tenant_id: tokenPayload.tenant_id,
      permissions: tokenPayload.permissions || [],
      email: email,
      full_name: 'User', // Will be updated on next getMe call
    };
    
    authService.setCurrentUser(userData);
    
    // Try to fetch full user data in background
    api.get('/auth/me', {
      headers: { Authorization: `Bearer ${access_token}` }
    }).then(meResponse => {
      authService.setCurrentUser(meResponse.data);
    }).catch(err => {
      console.warn('Could not fetch full user data:', err);
    });
    
    return userData;
  },
  
  logout: async () => {
    try {
      const token = authService.getToken();
      if (token) {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      authService.clearAuth();
    }
  },
  
  refreshToken: async () => {
    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    // Use a fresh axios instance to avoid interceptor loop
    const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, { 
      refresh_token: refreshToken 
    }, {
      headers: { 'Content-Type': 'application/json' }
    });
    
    const { access_token, refresh_token: newRefreshToken } = response.data;
    authService.setToken(access_token);
    
    // Update refresh token if a new one was provided
    if (newRefreshToken) {
      authService.setRefreshToken(newRefreshToken);
    }
    
    return access_token;
  },
  
  getMe: async () => {
    const response = await api.get('/auth/me', {
      headers: { Authorization: `Bearer ${authService.getToken()}` }
    });
    authService.setCurrentUser(response.data);
    return response.data;
  },
};

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors with automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if we have a refresh token
      const refreshToken = authService.getRefreshToken();
      if (!refreshToken) {
        // No refresh token - clear auth and redirect to login
        authService.clearAuth();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const newToken = await authService.refreshToken();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        authService.clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Tenant Service - tenant, outlet, franchisee management
 */
export const tenantService = {
  getOutlets: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/outlets`);
    return response.data;
  },
  
  getOutlet: async (tenantId, outletId) => {
    const response = await api.get(`/tenants/${tenantId}/outlets`);
    return response.data.find(o => o.id === outletId);
  },
  
  getFranchisees: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/franchisees`);
    return response.data;
  },
  
  getBrands: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/brands`);
    return response.data;
  },
};

/**
 * Menu Service - categories, products, modifiers
 */
export const menuService = {
  // Categories
  getCategories: async (tenantId, options = {}) => {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (options.channel) params.append('channel', options.channel);
    if (options.includeInactive) params.append('include_inactive', 'true');
    
    const response = await api.get(`/menu/categories?${params}`);
    return response.data;
  },
  
  getCategory: async (categoryId) => {
    const response = await api.get(`/menu/categories/${categoryId}`);
    return response.data;
  },
  
  createCategory: async (data) => {
    const response = await api.post('/menu/categories', data);
    return response.data;
  },
  
  updateCategory: async (categoryId, data) => {
    const response = await api.patch(`/menu/categories/${categoryId}`, data);
    return response.data;
  },
  
  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/menu/categories/${categoryId}`);
    return response.data;
  },
  
  // Products
  getProducts: async (tenantId, options = {}) => {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (options.categoryId) params.append('category_id', options.categoryId);
    if (options.channel) params.append('channel', options.channel);
    if (options.search) params.append('search', options.search);
    if (options.page) params.append('page', options.page);
    if (options.pageSize) params.append('page_size', options.pageSize);
    
    const response = await api.get(`/menu/products?${params}`);
    return response.data;
  },
  
  getProduct: async (productId) => {
    const response = await api.get(`/menu/products/${productId}`);
    return response.data;
  },
  
  createProduct: async (data) => {
    const response = await api.post('/menu/products', data);
    return response.data;
  },
  
  updateProduct: async (productId, data) => {
    const response = await api.patch(`/menu/products/${productId}`, data);
    return response.data;
  },
  
  deleteProduct: async (productId) => {
    const response = await api.delete(`/menu/products/${productId}`);
    return response.data;
  },
  
  // Modifier Groups
  getModifierGroups: async (tenantId) => {
    const response = await api.get(`/menu/modifier-groups?tenant_id=${tenantId}`);
    return response.data;
  },
  
  createModifierGroup: async (data) => {
    const response = await api.post('/menu/modifier-groups', data);
    return response.data;
  },
  
  // Menu Views
  getPOSMenu: async (outletId) => {
    const response = await api.get(`/menu/outlets/${outletId}/pos`);
    return response.data;
  },
  
  getTakeawayMenu: async (outletId) => {
    const response = await api.get(`/menu/outlets/${outletId}/takeaway`);
    return response.data;
  },
};

/**
 * POS Service - orders, tables, registers, sessions
 */
export const posService = {
  // Floor Plan & Tables
  getFloorPlan: async (outletId) => {
    const response = await api.get(`/pos/outlets/${outletId}/floor-plan`);
    return response.data;
  },
  
  updateTableState: async (tableId, state, guestCount) => {
    const response = await api.patch(`/pos/tables/${tableId}/state`, {
      state,
      current_guest_count: guestCount,
    });
    return response.data;
  },
  
  // Dashboard
  getDashboard: async (outletId, sessionId) => {
    const params = sessionId ? `?session_id=${sessionId}` : '';
    const response = await api.get(`/pos/outlets/${outletId}/dashboard${params}`);
    return response.data;
  },
  
  // Registers & Sessions
  getRegisters: async (tenantId, outletId) => {
    const params = outletId ? `&outlet_id=${outletId}` : '';
    const response = await api.get(`/pos/registers?tenant_id=${tenantId}${params}`);
    return response.data;
  },
  
  openSession: async (data) => {
    const response = await api.post('/pos/sessions', data);
    return response.data;
  },
  
  closeSession: async (sessionId, data) => {
    const response = await api.post(`/pos/sessions/${sessionId}/close`, data);
    return response.data;
  },
  
  // Orders
  getOrders: async (outletId, options = {}) => {
    const params = new URLSearchParams({ outlet_id: outletId });
    if (options.status) params.append('status', options.status);
    if (options.orderType) params.append('order_type', options.orderType);
    if (options.date) params.append('order_date', options.date);
    
    const response = await api.get(`/pos/orders?${params}`);
    return response.data;
  },
  
  getOrder: async (orderId) => {
    const response = await api.get(`/pos/orders/${orderId}`);
    return response.data;
  },
  
  createOrder: async (data) => {
    const response = await api.post('/pos/orders', data);
    return response.data;
  },
  
  updateOrder: async (orderId, data) => {
    const response = await api.patch(`/pos/orders/${orderId}`, data);
    return response.data;
  },
  
  addOrderLine: async (orderId, data) => {
    const response = await api.post(`/pos/orders/${orderId}/lines`, data);
    return response.data;
  },
  
  voidOrderLine: async (orderId, lineId, reason) => {
    const response = await api.delete(`/pos/orders/${orderId}/lines/${lineId}`, {
      data: { reason }
    });
    return response.data;
  },
  
  addPayment: async (orderId, data) => {
    const response = await api.post(`/pos/orders/${orderId}/payments`, data);
    return response.data;
  },
  
  // Quick Order (GO buttons)
  createQuickOrder: async (data) => {
    const response = await api.post('/pos/quick-order', data);
    return response.data;
  },
  
  getGOStatus: async (outletId) => {
    const response = await api.get(`/pos/outlets/${outletId}/go-status`);
    return response.data;
  },
  
  completeGOOrder: async (outletId, goNumber) => {
    const response = await api.post(`/pos/outlets/${outletId}/go-complete/${goNumber}`);
    return response.data;
  },
  
  // Payment Methods
  getPaymentMethods: async (tenantId, outletId) => {
    const params = outletId ? `&outlet_id=${outletId}` : '';
    const response = await api.get(`/pos/payment-methods?tenant_id=${tenantId}${params}`);
    return response.data;
  },
  
  // VAT Configuration
  getVATConfig: async (tenantId, country) => {
    const params = country ? `&country=${country}` : '';
    const response = await api.get(`/pos/vat-config?tenant_id=${tenantId}${params}`);
    return response.data;
  },
};

/**
 * Payment Service - Stripe Checkout integration
 */
export const paymentService = {
  // Create Stripe Checkout session for an order
  createCheckoutSession: async (orderId, outletId, currency = 'NOK') => {
    const originUrl = window.location.origin;
    const response = await api.post('/payments/checkout/session', {
      order_id: orderId,
      origin_url: originUrl,
      currency: currency,
    }, {
      headers: {
        'X-Outlet-Id': outletId,
      },
    });
    return response.data;
  },
  
  // Get checkout session status
  getCheckoutStatus: async (sessionId, outletId) => {
    const response = await api.get(`/payments/checkout/status/${sessionId}`, {
      headers: {
        'X-Outlet-Id': outletId,
      },
    });
    return response.data;
  },
  
  // List payment transactions
  getTransactions: async (outletId, options = {}) => {
    const params = new URLSearchParams();
    if (options.orderId) params.append('order_id', options.orderId);
    if (options.status) params.append('status_filter', options.status);
    if (options.limit) params.append('limit', options.limit);
    
    const response = await api.get(`/payments/transactions?${params}`, {
      headers: {
        'X-Outlet-Id': outletId,
      },
    });
    return response.data;
  },
  
  // Get single transaction
  getTransaction: async (transactionId, outletId) => {
    const response = await api.get(`/payments/transactions/${transactionId}`, {
      headers: {
        'X-Outlet-Id': outletId,
      },
    });
    return response.data;
  },
};

/**
 * Reservation Service - bookings, diary, availability
 */
export const reservationService = {
  // Diary View
  getDiary: async (outletId, date) => {
    console.log(`[ReservationService] getDiary called with outletId=${outletId}, date=${date}`);
    try {
      const response = await api.get(`/reservations/outlets/${outletId}/diary?view_date=${date}`);
      console.log(`[ReservationService] getDiary success:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[ReservationService] getDiary error:`, error.response?.status, error.message);
      throw error;
    }
  },
  
  // Availability
  checkAvailability: async (outletId, date, partySize, servicePeriod) => {
    const params = new URLSearchParams({
      date,
      party_size: partySize,
    });
    if (servicePeriod) params.append('service_period', servicePeriod);
    
    const response = await api.get(`/reservations/outlets/${outletId}/availability?${params}`);
    return response.data;
  },
  
  // Reservations
  getReservations: async (tenantId, outletId, options = {}) => {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (outletId) params.append('outlet_id', outletId);
    if (options.date) params.append('reservation_date', options.date);
    if (options.status) params.append('status', options.status);
    
    const response = await api.get(`/reservations?${params}`);
    return response.data;
  },
  
  getReservation: async (reservationId) => {
    const response = await api.get(`/reservations/${reservationId}`);
    return response.data;
  },
  
  createReservation: async (data) => {
    const response = await api.post('/reservations', data);
    return response.data;
  },
  
  updateReservation: async (reservationId, data) => {
    const response = await api.patch(`/reservations/${reservationId}`, data);
    return response.data;
  },
  
  updateReservationStatus: async (reservationId, status) => {
    const response = await api.patch(`/reservations/${reservationId}/status`, { status });
    return response.data;
  },
  
  seatReservation: async (reservationId, data) => {
    const response = await api.post(`/reservations/${reservationId}/seat`, data);
    return response.data;
  },
  
  cancelReservation: async (reservationId, reason) => {
    const response = await api.delete(`/reservations/${reservationId}`, {
      data: { reason }
    });
    return response.data;
  },
  
  // Walk-ins
  createWalkIn: async (data) => {
    const response = await api.post('/reservations/walk-in', data);
    return response.data;
  },
  
  // Service Periods
  getServicePeriods: async (tenantId, outletId) => {
    const params = outletId ? `&outlet_id=${outletId}` : '';
    const response = await api.get(`/reservations/service-periods?tenant_id=${tenantId}${params}`);
    return response.data;
  },
};

/**
 * Inventory Service - stock alerts, materials, purchase orders
 */
export const inventoryService = {
  // Get stock alerts for low/critical items
  getStockAlerts: async (outletId) => {
    const response = await api.get(`/inventory/stock/alerts?outlet_id=${outletId}`);
    return response.data;
  },
  
  // Get all raw materials
  getMaterials: async (tenantId, options = {}) => {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (options.categoryId) params.append('category_id', options.categoryId);
    if (options.isActive !== undefined) params.append('is_active', options.isActive);
    if (options.search) params.append('search', options.search);
    
    const response = await api.get(`/inventory/materials?${params}`);
    return response.data;
  },
  
  // Get stock levels for an outlet
  getStockLevels: async (outletId, options = {}) => {
    const params = new URLSearchParams({ outlet_id: outletId });
    if (options.belowReorder) params.append('below_reorder', 'true');
    if (options.categoryId) params.append('category_id', options.categoryId);
    
    const response = await api.get(`/inventory/stock?${params}`);
    return response.data;
  },
  
  // Get pending purchase orders
  getPurchaseOrders: async (tenantId, options = {}) => {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (options.outletId) params.append('outlet_id', options.outletId);
    if (options.status) params.append('status', options.status);
    
    const response = await api.get(`/inventory/purchase-orders?${params}`);
    return response.data;
  },
};

/**
 * Finance Service - expenses, approvals, reports
 */
export const financeService = {
  // Get pending expense approvals
  getPendingApprovals: async (tenantId, options = {}) => {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (options.outletId) params.append('outlet_id', options.outletId);
    if (options.status) params.append('status', options.status || 'pending');
    
    const response = await api.get(`/finance/expenses?${params}`);
    return response.data;
  },
  
  // Approve or reject expense
  approveExpense: async (expenseId, approved, notes) => {
    const response = await api.post(`/finance/expenses/${expenseId}/approve`, {
      approved,
      notes
    });
    return response.data;
  },
  
  // Get P&L report
  getPLReport: async (outletId, startDate, endDate) => {
    const params = new URLSearchParams({
      outlet_id: outletId,
      start_date: startDate,
      end_date: endDate,
    });
    const response = await api.get(`/finance/reports/pl?${params}`);
    return response.data;
  },
};

// Export the api instance for custom requests
export default api;
